import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, FileImage, AlertTriangle, UserPlus, Users, Search, Camera, Bone, HeartPulse, CircleDot } from 'lucide-react';
import { toast } from 'sonner';

const BODY_PARTS = [
  'Wrist (right)', 'Wrist (left)', 'Hand / Fingers (right)', 'Hand / Fingers (left)',
  'Forearm (right)', 'Forearm (left)', 'Elbow (right)', 'Elbow (left)',
  'Humerus / Upper arm (right)', 'Humerus / Upper arm (left)',
  'Shoulder (right)', 'Shoulder (left)', 'Clavicle',
  'Ankle (right)', 'Ankle (left)', 'Foot / Toes (right)', 'Foot / Toes (left)',
  'Tibia-Fibula / Leg (right)', 'Tibia-Fibula / Leg (left)',
  'Knee (right)', 'Knee (left)', 'Femur / Thigh (right)', 'Femur / Thigh (left)',
  'Hip / Pelvis', 'Spine (cervical)', 'Spine (thoracic)', 'Spine (lumbar)',
  'Ribs', 'Skull / Facial bones', 'Other',
];

const CASE_TYPES = [
  { id: 'chest_xray', label: 'Chest X-Ray', desc: 'Pneumonia, TB, effusion, cardiomegaly & more', icon: HeartPulse, color: '#1A4D2E' },
  { id: 'extremity', label: 'Extremity X-Ray', desc: 'Fractures, dislocations, arthritis, foreign bodies', icon: Bone, color: '#B45309', badge: 'NEW' },
  { id: 'abdominal', label: 'Abdominal X-Ray', desc: 'Obstruction, free air, renal stones, ileus', icon: CircleDot, color: '#3B82F6', badge: 'NEW' },
];

function normalizeType(t) {
  if (t === 'bone_fracture') return 'extremity';
  return CASE_TYPES.find(c => c.id === t) ? t : 'chest_xray';
}

export default function NewCasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPid = searchParams.get('patientId');
  const preselectedType = normalizeType(searchParams.get('type'));
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const [caseType, setCaseType] = useState(preselectedType);
  const isExtremity = caseType === 'extremity';
  const isAbdominal = caseType === 'abdominal';

  const [mode, setMode] = useState(preselectedPid ? 'existing' : 'new');
  const [form, setForm] = useState({ patient_name: '', patient_age: '', patient_gender: '', clinical_notes: '' });
  const [bodyPart, setBodyPart] = useState('');
  const [clinicalQuery, setClinicalQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/patients').then(r => setPatients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preselectedPid && patients.length > 0) {
      const p = patients.find(pt => pt.patient_id === preselectedPid);
      if (p) selectPatient(p);
    }
  }, [preselectedPid, patients]);

  const selectPatient = (p) => {
    setSelectedPatient(p);
    setForm({ patient_name: p.patient_name, patient_age: String(p.patient_age), patient_gender: p.patient_gender, clinical_notes: '' });
    setShowPatientDropdown(false);
    setPatientSearch('');
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setForm({ patient_name: '', patient_age: '', patient_gender: '', clinical_notes: '' });
    setMode('new');
  };

  const filteredPatients = patients.filter(p =>
    p.patient_name?.toLowerCase().includes(patientSearch.toLowerCase()) ||
    p.patient_id?.toLowerCase().includes(patientSearch.toLowerCase())
  );

  const handleFile = (f) => {
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(f.type)) {
      setError('Only PNG/JPG images are accepted'); return;
    }
    setFile(f);
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please upload an X-ray image'); return; }
    if (!form.patient_name || !form.patient_age || !form.patient_gender) {
      setError('Please fill in all required fields'); return;
    }
    if (isExtremity && !bodyPart) {
      setError('Please select the body part being imaged'); return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('patient_name', form.patient_name);
      fd.append('patient_age', form.patient_age);
      fd.append('patient_gender', form.patient_gender);
      fd.append('clinical_notes', form.clinical_notes);
      fd.append('case_type', caseType);
      if (isExtremity) fd.append('body_part', bodyPart);
      if (isAbdominal) fd.append('clinical_query', clinicalQuery);
      if (selectedPatient) fd.append('existing_patient_id', selectedPatient.patient_id);
      fd.append('xray', file);
      const { data } = await api.post('/cases', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Case created — ${data.patient_id}`);
      navigate(`/case/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  const activeType = CASE_TYPES.find(c => c.id === caseType);
  const pageTitle = `New ${activeType.label} Case`;
  const pageDesc = `Upload ${activeType.label.toLowerCase()} image and enter patient details`;
  const notesLabel = isExtremity ? 'Injury Mechanism / Symptoms' : isAbdominal ? 'History / Additional Notes' : 'Clinical Notes';
  const notesPlaceholder = isExtremity
    ? 'How did the injury occur? (e.g. fall on outstretched hand, RTA, sports injury). Pain, swelling, deformity...'
    : isAbdominal
      ? 'Bowel habits, vomiting, distension, tenderness, medications, prior surgery...'
      : 'Symptoms, history, reason for X-ray...';

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-case-page">
      <div>
        <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900" data-testid="new-case-title">{pageTitle}</h1>
        <p className="text-stone-500 mt-1">{pageDesc}</p>
      </div>

      {/* Case type selector (3 modules) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="case-type-selector">
        {CASE_TYPES.map(ct => {
          const Icon = ct.icon;
          const selected = caseType === ct.id;
          return (
            <button
              key={ct.id}
              type="button"
              data-testid={`case-type-${ct.id}`}
              onClick={() => setCaseType(ct.id)}
              className={`text-left rounded-xl border-2 p-4 transition-all ${selected ? 'shadow-sm' : 'border-stone-200 hover:border-stone-300 bg-white'}`}
              style={selected ? { borderColor: ct.color, backgroundColor: `${ct.color}0F` } : {}}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white`} style={{ backgroundColor: selected ? ct.color : '#e7e5e4', color: selected ? '#fff' : '#78716c' }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-stone-900 text-sm flex items-center gap-2">
                    <span>{ct.label}</span>
                    {ct.badge && <Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">{ct.badge}</Badge>}
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">{ct.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <span>AI analysis is for decision support only. Always apply your clinical expertise{isExtremity ? ' and consider orthopaedic referral if needed' : isAbdominal ? ' and consider surgical/GI referral if free air or obstruction suspected' : ''} for the final diagnosis.</span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload */}
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading">{activeType.label}</CardTitle>
              <CardDescription>Upload PNG or JPG image</CardDescription>
            </CardHeader>
            <CardContent>
              {preview ? (
                <div className="relative">
                  <div className="bg-stone-950 rounded-xl overflow-hidden border border-stone-800">
                    <img src={preview} alt="X-ray preview" className="w-full h-64 object-contain" />
                  </div>
                  <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white">
                    <X className="w-4 h-4 text-stone-600" />
                  </button>
                  <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
                    <FileImage className="w-3 h-3" /> {file.name} ({(file.size / 1024).toFixed(0)} KB)
                  </p>
                </div>
              ) : (
                <>
                  <div
                    data-testid="xray-upload-zone"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-[#1A4D2E] bg-[#1A4D2E]/5' : 'border-stone-300 hover:border-stone-400 hover:bg-stone-50'}`}
                  >
                    <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-stone-700">Drop X-ray image here or click to browse</p>
                    <p className="text-xs text-stone-400 mt-1">PNG or JPG format</p>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={e => handleFile(e.target.files[0])} className="hidden" />
                  </div>
                  <button
                    type="button"
                    data-testid="camera-capture-btn"
                    onClick={() => cameraRef.current?.click()}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#1A4D2E]/5 transition-colors text-sm font-medium"
                  >
                    <Camera className="w-5 h-5" />
                    Capture with Camera
                  </button>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={e => handleFile(e.target.files[0])} className="hidden" />
                </>
              )}
            </CardContent>
          </Card>

          {/* Patient Info */}
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading">Patient Details</CardTitle>
              <CardDescription>Select existing patient or create new</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button type="button" variant={mode === 'existing' ? 'default' : 'outline'} size="sm"
                  onClick={() => setMode('existing')}
                  className={`gap-2 flex-1 ${mode === 'existing' ? 'bg-[#1A4D2E] hover:bg-[#143B23] text-white' : 'border-stone-200'}`}
                  data-testid="mode-existing-btn"
                >
                  <Users className="w-4 h-4" /> Existing Patient
                </Button>
                <Button type="button" variant={mode === 'new' ? 'default' : 'outline'} size="sm"
                  onClick={() => { clearPatient(); setMode('new'); }}
                  className={`gap-2 flex-1 ${mode === 'new' ? 'bg-[#1A4D2E] hover:bg-[#143B23] text-white' : 'border-stone-200'}`}
                  data-testid="mode-new-btn"
                >
                  <UserPlus className="w-4 h-4" /> New Patient
                </Button>
              </div>

              {mode === 'existing' && !selectedPatient && (
                <div className="relative">
                  <Label className="text-stone-700">Search Patient</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      data-testid="patient-search-input"
                      value={patientSearch}
                      onChange={e => { setPatientSearch(e.target.value); setShowPatientDropdown(true); }}
                      onFocus={() => setShowPatientDropdown(true)}
                      placeholder="Search by name or Patient ID..."
                      className="pl-9"
                    />
                  </div>
                  {showPatientDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {filteredPatients.length === 0 ? (
                        <div className="p-3 text-sm text-stone-500 text-center">No patients found</div>
                      ) : filteredPatients.map(p => (
                        <button key={p.patient_id} type="button"
                          onClick={() => selectPatient(p)}
                          data-testid={`select-patient-${p.patient_id}`}
                          className="w-full text-left px-3 py-2 hover:bg-stone-50 flex items-center justify-between border-b border-stone-50 last:border-0"
                        >
                          <div>
                            <p className="text-sm font-medium text-stone-900">{p.patient_name}</p>
                            <p className="text-xs text-stone-400">{p.patient_age}y, {p.patient_gender} — {p.case_count} case(s)</p>
                          </div>
                          <Badge variant="outline" className="font-mono text-xs">{p.patient_id}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedPatient && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#1A4D2E]/5 border border-[#1A4D2E]/20">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{selectedPatient.patient_name}</p>
                    <p className="text-xs text-stone-500">{selectedPatient.patient_id} — {selectedPatient.case_count} previous case(s)</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={clearPatient} className="text-stone-500">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <div>
                <Label htmlFor="pname" className="text-stone-700">Patient Name *</Label>
                <Input data-testid="patient-name-input" id="pname" value={form.patient_name} onChange={e => setForm({...form, patient_name: e.target.value})} placeholder="Patient full name" required className="mt-1" disabled={!!selectedPatient} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="page" className="text-stone-700">Age *</Label>
                  <Input data-testid="patient-age-input" id="page" type="number" min="0" max="150" value={form.patient_age} onChange={e => setForm({...form, patient_age: e.target.value})} placeholder="35" required className="mt-1" disabled={!!selectedPatient} />
                </div>
                <div>
                  <Label className="text-stone-700">Gender *</Label>
                  <Select value={form.patient_gender} onValueChange={v => setForm({...form, patient_gender: v})} disabled={!!selectedPatient}>
                    <SelectTrigger data-testid="patient-gender-select" className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isExtremity && (
                <div>
                  <Label className="text-stone-700">Body Part Imaged *</Label>
                  <Select value={bodyPart} onValueChange={setBodyPart}>
                    <SelectTrigger data-testid="body-part-select" className="mt-1">
                      <SelectValue placeholder="Select body part..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {BODY_PARTS.map(bp => (
                        <SelectItem key={bp} value={bp}>{bp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isAbdominal && (
                <div>
                  <Label htmlFor="query" className="text-stone-700">Clinical Query / Suspected Pathology</Label>
                  <Input
                    data-testid="clinical-query-input"
                    id="query"
                    value={clinicalQuery}
                    onChange={e => setClinicalQuery(e.target.value)}
                    placeholder="e.g. rule out obstruction, suspected renal colic, free air, ileus..."
                    className="mt-1"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="notes" className="text-stone-700">{notesLabel}</Label>
                <Textarea data-testid="clinical-notes-input" id="notes" value={form.clinical_notes} onChange={e => setForm({...form, clinical_notes: e.target.value})} placeholder={notesPlaceholder} rows={4} className="mt-1" />
              </div>
            </CardContent>
          </Card>
        </div>

        {error && <div data-testid="case-error" className="mt-4 text-sm text-[#8B2626] bg-red-50 p-3 rounded-lg">{error}</div>}

        <div className="mt-6 flex gap-3">
          <Button data-testid="create-case-btn" type="submit" disabled={loading} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white px-8 h-11 rounded-lg">
            {loading ? 'Creating...' : 'Create Case & Upload'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/')} className="h-11 rounded-lg border-stone-200">Cancel</Button>
        </div>
      </form>
    </div>
  );
}
